/**
 * Shared serialization for loan program data / application form fields (CreateProgram, ApplicationFormView).
 */

export const BACKEND_FILE_FIELD_TYPES = ['attach_file', 'digital_signature'];

const MEASUREMENT_UNIT_TYPES = new Set([
    'area', 'length', 'weight', 'volume', 'temperature', 'distance', 'time',
    'dimension', 'mass', 'speed', 'pressure', 'other',
]);

const DEFAULT_ATTACH_FILE_OPTIONS = { allowedFormats: ['pdf', 'jpg', 'png'] };

export function normalizeFieldType(dataType) {
    const t = (dataType || '').replace(/-/g, '_');
    return t === 'date' ? 'date_field' : t;
}

export function getAllowedFileTypesFromOptions(options) {
    if (!options || typeof options !== 'object') return [];
    if (Array.isArray(options.allowedFileTypes)) return options.allowedFileTypes;
    if (typeof options.allowedFileTypes === 'string') return [options.allowedFileTypes];
    if (Array.isArray(options.allowedFormats)) {
        const hasImage = options.allowedFormats.some(f => ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(String(f).toLowerCase()));
        const hasVideo = options.allowedFormats.some(f => ['mp4', 'mov', 'webm'].includes(String(f).toLowerCase()));
        const hasDocx = options.allowedFormats.some(f => ['doc', 'docx'].includes(String(f).toLowerCase()));
        return [
            ...(hasImage ? ['image'] : []),
            ...(hasVideo ? ['video'] : []),
            ...(hasDocx ? ['docx'] : []),
        ];
    }
    return [];
}

function coerceBooleanToggle(value, activeToken) {
    if (Array.isArray(value)) return activeToken ? value.includes(activeToken) : value.length > 0;
    if (typeof value === 'string') return value.toLowerCase() === 'true';
    return !!value;
}

export function getChoiceOptions(field) {
    if (Array.isArray(field.options)) return field.options.map(o => (typeof o === 'object' && o && o.value !== undefined ? o.value : o));
    if (field.options && Array.isArray(field.options.choices)) return field.options.choices;
    return [];
}

function getAttachFileOptions(field) {
    const allowedFileTypes = Array.isArray(field.allowedFileTypes) ? field.allowedFileTypes : [];
    const allowedFormats = Array.isArray(field.allowedFormats)
        ? field.allowedFormats
        : [];
    const mappedFormats = [];
    if (allowedFileTypes.includes('image')) mappedFormats.push('jpg', 'jpeg', 'png', 'gif');
    if (allowedFileTypes.includes('video')) mappedFormats.push('mp4');
    if (allowedFileTypes.includes('docx')) mappedFormats.push('docx');
    const mergedFormats = [...new Set([...DEFAULT_ATTACH_FILE_OPTIONS.allowedFormats, ...allowedFormats, ...mappedFormats])];
    const options = {
        allowedFormats: mergedFormats.length ? mergedFormats : [...DEFAULT_ATTACH_FILE_OPTIONS.allowedFormats],
    };
    if (allowedFileTypes.length) {
        options.allowedFileTypes = allowedFileTypes.length === 1 ? allowedFileTypes[0] : allowedFileTypes;
    }
    return options;
}

function getMeasurementOptions(field) {
    let unitType = String(field.unitType || '').trim().toLowerCase();
    if (!unitType || !MEASUREMENT_UNIT_TYPES.has(unitType)) {
        unitType = 'other';
    }
    const units = Array.isArray(field.units) && field.units.length
        ? [...field.units]
        : (field.measurementType ? [field.measurementType] : []);
    return {
        unitType,
        units,
        allowUserToChangeUnit: !!(field.allowUserToChangeUnit ?? field.allowUserToChangeUnitType),
    };
}

function getDigitalSignatureOptions(field) {
    let allowDigitalSignature = coerceBooleanToggle(field.digitalSignature, 'digital-signature');
    let allowFileUpload = coerceBooleanToggle(field.attachFile, 'attach-file');
    if (!allowDigitalSignature && !allowFileUpload) {
        allowDigitalSignature = true;
    }
    return { allowDigitalSignature, allowFileUpload };
}

function sanitizeOutgoingValueString(str) {
    if (str == null) return '';
    const s = String(str).trim();
    if (!s) return '';
    const upper = s.toUpperCase();
    if (upper === 'NOT PROVIDED YET' || upper === 'NOT_PROVIDED' || upper === 'NOT PROVIDED') {
        return '';
    }
    return String(str);
}

function formatFieldValue(field) {
    const v = field.value;
    if (field.dataType === 'check_boxes' && Array.isArray(v)) {
        return v.length ? v.join(',') : '';
    }
    if (v !== undefined && v !== null && v !== '') {
        if (v instanceof File) return '';
        return typeof v === 'object' && v instanceof Date ? v.toISOString().slice(0, 10) : String(v);
    }
    return '';
}

export function parseNumericFieldValue(raw) {
    if (raw === undefined || raw === null || raw === '') return null;
    const n = typeof raw === 'number' ? raw : parseFloat(String(raw).replace(/,/g, ''));
    return !Number.isNaN(n) && Number.isFinite(n) ? n : null;
}

function safeMultipartFilename(title) {
    const base = String(title || 'upload').replace(/[^\w\s.-]/g, '').trim() || 'upload';
    return base.slice(0, 80);
}

function dataUrlToBlob(dataUrl) {
    if (typeof dataUrl !== 'string' || !dataUrl.startsWith('data:')) return null;
    const parts = dataUrl.split(',');
    if (parts.length < 2) return null;
    const mimeMatch = parts[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
    try {
        const binary = atob(parts[1]);
        const len = binary.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i += 1) bytes[i] = binary.charCodeAt(i);
        return new Blob([bytes], { type: mime });
    } catch (_e) {
        return null;
    }
}

function dataUrlToUploadableFile(dataUrl, filename) {
    const blob = dataUrlToBlob(dataUrl);
    if (!blob) return null;
    try {
        return new File([blob], filename, { type: blob.type || 'image/png' });
    } catch (_e) {
        return blob;
    }
}

export function getMultipartFileForDynamicField(field) {
    const t = normalizeFieldType(field.dataType);
    if (t === 'attach_file') {
        if (field.value instanceof File) return field.value;
        return null;
    }
    if (t === 'digital_signature') {
        if (field.signatureFile instanceof File) return field.signatureFile;
        if (field.value instanceof File) return field.value;
        if (typeof field.value === 'string' && field.value.startsWith('data:')) {
            const name = `${safeMultipartFilename(field.fieldTitle)}.png`;
            return dataUrlToUploadableFile(field.value, name);
        }
        return null;
    }
    return null;
}

/**
 * New upload File OR persisted value string from API (URL/path).
 * Mandatory checks must pass on edit without forcing re-upload.
 */
export function hasMandatoryDynamicFileProvided(field) {
    if (!field) return false;
    const t = normalizeFieldType(field.dataType);
    if (!BACKEND_FILE_FIELD_TYPES.includes(t)) return false;
    if (getMultipartFileForDynamicField(field)) return true;
    if (t === 'attach_file') {
        const s = sanitizeOutgoingValueString(formatFieldValue(field));
        return s.length > 0;
    }
    if (t === 'digital_signature') {
        const v = field.value;
        if (typeof v !== 'string') return false;
        const s = v.trim();
        if (!s) return false;
        if (/^https?:\/\//i.test(s)) return true;
        if (s.startsWith('/') || s.startsWith('//')) return true;
    }
    return false;
}

export function buildFieldPayload(field, opts = {}) {
    const { includeValue = true, omitFileValueForMultipart = false } = opts;
    const fieldTypeBackend = normalizeFieldType(field.dataType);
    const payload = {
        fieldName: field.fieldTitle || '',
        fieldType: fieldTypeBackend,
        isMandetory: !!(field.isMandetory ?? field.mandatory ?? field.isMandatory),
        helpText: field.helpText == null ? '' : String(field.helpText),
    };

    const choices = getChoiceOptions(field);
    const choicesArr = Array.isArray(choices) ? choices : [];

    if (fieldTypeBackend === 'radio_button' || fieldTypeBackend === 'check_boxes') {
        payload.options = { choices: choicesArr };
    } else if (fieldTypeBackend === 'dropdown') {
        payload.options = {
            choices: choicesArr,
            allowMultipleChoices: !!field.allowMultipleInputs,
        };
    }

    if (fieldTypeBackend === 'attach_file') {
        payload.options = getAttachFileOptions(field);
    }
    if (fieldTypeBackend === 'measurement_unit') {
        payload.options = getMeasurementOptions(field);
    }
    if (fieldTypeBackend === 'digital_signature') {
        payload.options = getDigitalSignatureOptions(field);
    }

    if (!includeValue) {
        return payload;
    }

    if (fieldTypeBackend === 'attach_file' || fieldTypeBackend === 'digital_signature') {
        if (omitFileValueForMultipart) {
            return payload;
        }
        const urlOrEmpty = sanitizeOutgoingValueString(formatFieldValue(field));
        payload.value = urlOrEmpty;
        return payload;
    }
    if (fieldTypeBackend === 'measurement_unit') {
        const unitList = Array.isArray(field.units) && field.units.length
            ? field.units
            : (field.measurementType ? [field.measurementType] : []);
        const unit = field.measurementType || unitList[0] || '';
        const n = parseNumericFieldValue(field.value);
        if (n !== null) {
            payload.value = { amount: n, unit: String(unit || '') };
        } else {
            payload.value = '';
        }
        return payload;
    }
    if (fieldTypeBackend === 'text_field_numeric') {
        const n = parseNumericFieldValue(field.value);
        payload.value = n !== null ? n : '';
        return payload;
    }
    if (fieldTypeBackend === 'check_boxes') {
        payload.value = sanitizeOutgoingValueString(formatFieldValue(field));
        return payload;
    }
    if (fieldTypeBackend === 'dropdown') {
        const v = field.value;
        if (field.allowMultipleInputs) {
            const parts = Array.isArray(v) ? v : (v !== undefined && v !== null && v !== '' ? [v] : []);
            payload.value = sanitizeOutgoingValueString(
                parts.length ? parts.map((x) => String(x)).join(',') : '',
            );
        } else {
            payload.value = v === undefined || v === null ? '' : String(v);
        }
        return payload;
    }
    if (fieldTypeBackend === 'radio_button') {
        payload.value = field.value === undefined || field.value === null ? '' : String(field.value);
        return payload;
    }
    if (fieldTypeBackend === 'date_field') {
        const v = field.value;
        if (v instanceof Date) {
            payload.value = v.toISOString().slice(0, 10);
        } else {
            payload.value = sanitizeOutgoingValueString(formatFieldValue(field));
        }
        return payload;
    }

    payload.value = sanitizeOutgoingValueString(formatFieldValue(field));
    return payload;
}

export function processDataFields(fields, opts = {}) {
    const { includeValue = true } = opts;
    const flatFields = [];
    const groupedFields = [];
    const fileFields = [];

    const pushPayload = (f) => {
        if (!includeValue) {
            flatFields.push(buildFieldPayload(f, { includeValue: false }));
            return;
        }
        const t = normalizeFieldType(f.dataType);
        let multipart = null;
        if (BACKEND_FILE_FIELD_TYPES.includes(t)) {
            multipart = getMultipartFileForDynamicField(f);
            if (multipart) {
                fileFields.push({ name: f.fieldTitle, file: multipart });
            }
        }
        const omitFile = BACKEND_FILE_FIELD_TYPES.includes(t) && !!multipart;
        flatFields.push(buildFieldPayload(f, { includeValue: true, omitFileValueForMultipart: omitFile }));
    };

    (fields || []).forEach(field => {
        if (field.isGroup) {
            const groupDef = {
                groupName: field.groupTitle,
                helpText: field.helpText || '',
                fields: [],
            };
            (field.groupFields || []).forEach(sub => {
                if (!includeValue) {
                    groupDef.fields.push(buildFieldPayload(sub, { includeValue: false }));
                    return;
                }
                const t = normalizeFieldType(sub.dataType);
                let multipart = null;
                if (BACKEND_FILE_FIELD_TYPES.includes(t)) {
                    multipart = getMultipartFileForDynamicField(sub);
                    if (multipart) {
                        fileFields.push({ name: sub.fieldTitle, file: multipart });
                    }
                }
                const omitFile = BACKEND_FILE_FIELD_TYPES.includes(t) && !!multipart;
                groupDef.fields.push(buildFieldPayload(sub, {
                    includeValue: true,
                    omitFileValueForMultipart: omitFile,
                }));
            });
            groupedFields.push(groupDef);
        } else {
            pushPayload(field);
        }
    });

    return { flatFields, groupedFields, fileFields };
}

/**
 * Map API loan program fields into AddDataField / StepTwo shape.
 * @param {Array} apiFields
 * @param {boolean} includeValue - program detail fields need values; application form schema often does not
 */
export function transformApiFieldsToForm(apiFields, includeValue) {
    if (!apiFields || !apiFields.length) return [];
    const ungrouped = [];
    const groupMap = {};
    const apiTypeToStepType = (t) => (t || '').replace(/-/g, '_');

    for (const field of apiFields) {
        const dataType = normalizeFieldType(apiTypeToStepType(field.fieldType));
        const rawOptions = field.options && typeof field.options === 'object' ? field.options : null;
        let value = null;
        let measurementTypeOverride = null;
        if (includeValue) {
            if (dataType === 'measurement_unit') {
                const nested = rawOptions?.value && typeof rawOptions.value === 'object' && !Array.isArray(rawOptions.value)
                    ? rawOptions.value
                    : null;
                if (nested) {
                    value = nested.amount !== undefined && nested.amount !== null && nested.amount !== ''
                        ? nested.amount
                        : '';
                    if (nested.unit != null && nested.unit !== '') {
                        measurementTypeOverride = String(nested.unit);
                    }
                } else if (field.value !== undefined && field.value !== null) {
                    const fv = field.value;
                    if (typeof fv === 'object' && !Array.isArray(fv) && ('amount' in fv || 'unit' in fv)) {
                        value = fv.amount !== undefined && fv.amount !== null && fv.amount !== ''
                            ? fv.amount
                            : '';
                        if (fv.unit != null && fv.unit !== '') {
                            measurementTypeOverride = String(fv.unit);
                        }
                    } else {
                        value = fv;
                    }
                } else {
                    value = '';
                }
            } else if (field.value !== undefined && field.value !== null) {
                value = dataType === 'check_boxes' && !Array.isArray(field.value)
                    ? (field.value ? String(field.value).split(',').map(s => s.trim()) : [])
                    : field.value;
            } else {
                value = dataType === 'check_boxes' ? [] : '';
            }
        }
        const normalizedChoiceOptions = Array.isArray(field.options)
            ? field.options
            : (Array.isArray(rawOptions?.choices) ? rawOptions.choices : []);
        const mapped = {
            _uid: `field-${field.id || Date.now()}-${Math.random().toString(36).slice(2)}`,
            fieldTitle: field.fieldName || '',
            dataType,
            isMandetory: !!(field.isMandetory),
            isGroup: false,
            options: normalizedChoiceOptions,
            value,
            helpText: field.helpText || '',
            allowMultipleInputs: !!rawOptions?.allowMultipleChoices,
            allowedFileTypes: getAllowedFileTypesFromOptions(rawOptions),
            allowedFormats: Array.isArray(rawOptions?.allowedFormats) ? rawOptions.allowedFormats : [],
            unitType: rawOptions?.unitType || null,
            measurementType: measurementTypeOverride
                || (Array.isArray(rawOptions?.units) && rawOptions.units.length ? rawOptions.units[0] : null),
            units: Array.isArray(rawOptions?.units) ? rawOptions.units : [],
            allowUserToChangeUnitType: !!rawOptions?.allowUserToChangeUnit,
            digitalSignature: !!rawOptions?.allowDigitalSignature,
            attachFile: !!rawOptions?.allowFileUpload,
        };
        if (dataType === 'date_field') {
            mapped._dateMenu = false;
        }
        if (field.groupId && field.group) {
            if (!groupMap[field.groupId]) {
                groupMap[field.groupId] = {
                    _uid: `group-${field.groupId}`,
                    isGroup: true,
                    groupTitle: field.group.groupName || '',
                    helpText: field.group.helpText || '',
                    groupFields: [],
                };
            }
            groupMap[field.groupId].groupFields.push(mapped);
        } else {
            ungrouped.push(mapped);
        }
    }
    return [...ungrouped, ...Object.values(groupMap)];
}
